import { useState, useEffect } from 'react';
import Layout from '../../components/layout/Layout';
import { api, imgUrl } from '../../api/api';
import toast from 'react-hot-toast';
import {
  Box, Typography, Button, Paper, TextField, Grid, Chip, Dialog,
  DialogTitle, DialogContent, DialogActions, FormControl, InputLabel,
  Select, MenuItem, IconButton, CircularProgress, Tabs, Tab,
  Table, TableBody, TableCell, TableHead, TableRow, Checkbox,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ImageIcon from '@mui/icons-material/Image';
import PreviewIcon from '@mui/icons-material/Preview';
import CampaignIcon from '@mui/icons-material/Campaign';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import TuneIcon from '@mui/icons-material/Tune';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SendIcon from '@mui/icons-material/Send';
import SeasonalTabs from './SeasonalTabs';

const CAMPAIGNS = [
  { key: '2026_pistachio', label: 'Pistachio Perfected 2026', color: '#6B7F3B', htmlPanel: '/seasonal_assets/html_panels/pistachio_panel.html' },
  { key: '2026_cloud', label: 'Cloud Series 2026', color: '#87CEEB', htmlPanel: '/seasonal_assets/html_panels/cloud_panel.html' },
  { key: '2025_winter_holiday', label: 'Winter Holiday 2025', color: '#B71C1C', htmlPanel: '/seasonal_assets/html_panels/holiday_panel.html' },
  { key: '2025_fall', label: 'Fall 2025', color: '#E65100', htmlPanel: '/seasonal_assets/html_panels/fall_panel.html' },
  { key: '2025_valentine', label: 'Valentine 2025', color: '#E91E63', htmlPanel: '/seasonal_assets/html_panels/valentine_panel.html' },
  { key: '2025_summer', label: 'Summer Refresh 2025', color: '#FF9800', htmlPanel: '/seasonal_assets/html_panels/summer_panel.html' },
];

const EMPTY_FORM = {
  name: '', campaign: '2026_pistachio', code: '',
  prices: { hot_s: '', hot_m: '', ice_s: '', ice_m: '' },
  calories: { hot_s: '', hot_m: '', ice_s: '', ice_m: '' },
};

function SeasonalProducts({ user }) {
  const [tab, setTab] = useState(0); // 0: 캠페인, 1: 제품관리, 2: 매장 커스텀, 3: 영상
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCampaign, setFilterCampaign] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [imageFile, setImageFile] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [generating, setGenerating] = useState(false);

  // 매장 커스텀
  const [stores, setStores] = useState([]);
  const [selectedStores, setSelectedStores] = useState([]);
  const [customProducts, setCustomProducts] = useState([]); // 커스텀 패널에 넣을 제품
  const [storeSearch, setStoreSearch] = useState('');

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const q = filterCampaign ? `?campaign=${filterCampaign}` : '';
      const res = await api.get(`/seasonal-products${q}`);
      setProducts(Array.isArray(res.data?.products) ? res.data.products : Array.isArray(res.data) ? res.data : []);
    } catch (_) {}
    setLoading(false);
  };

  const fetchStores = async () => {
    try {
      const userId = user?._id || user?.user?._id || '';
      const { data } = await api.get(`/store/getall/${userId}`);
      const list = data?.stores || data || [];
      setStores(Array.isArray(list) ? list : []);
    } catch (_) { setStores([]); }
  };

  useEffect(() => { fetchProducts(); }, [filterCampaign]);
  useEffect(() => { if (tab === 2) fetchStores(); }, [tab]);

  const openAdd = () => { setEditId(null); setForm(EMPTY_FORM); setImageFile(null); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditId(p._id);
    setForm({
      name: p.name, campaign: p.campaign, code: p.code || '',
      prices: { hot_s: p.prices?.hot_s ? (p.prices.hot_s / 100).toFixed(2) : '', hot_m: p.prices?.hot_m ? (p.prices.hot_m / 100).toFixed(2) : '', ice_s: p.prices?.ice_s ? (p.prices.ice_s / 100).toFixed(2) : '', ice_m: p.prices?.ice_m ? (p.prices.ice_m / 100).toFixed(2) : '' },
      calories: { hot_s: p.calories?.hot_s || '', hot_m: p.calories?.hot_m || '', ice_s: p.calories?.ice_s || '', ice_m: p.calories?.ice_m || '' },
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('상품명을 입력하세요');
    const payload = {
      ...form, code: form.code || form.name.toLowerCase().replace(/\s+/g, '_'),
      campaignLabel: CAMPAIGNS.find(c => c.key === form.campaign)?.label || form.campaign,
      prices: { hot_s: form.prices.hot_s ? Math.round(parseFloat(form.prices.hot_s) * 100) : undefined, hot_m: form.prices.hot_m ? Math.round(parseFloat(form.prices.hot_m) * 100) : undefined, ice_s: form.prices.ice_s ? Math.round(parseFloat(form.prices.ice_s) * 100) : undefined, ice_m: form.prices.ice_m ? Math.round(parseFloat(form.prices.ice_m) * 100) : undefined },
      calories: { hot_s: form.calories.hot_s ? Number(form.calories.hot_s) : undefined, hot_m: form.calories.hot_m ? Number(form.calories.hot_m) : undefined, ice_s: form.calories.ice_s ? Number(form.calories.ice_s) : undefined, ice_m: form.calories.ice_m ? Number(form.calories.ice_m) : undefined },
    };
    try {
      if (editId) { await api.patch(`/seasonal-products/${editId}`, payload); toast.success('수정완료'); }
      else { const res = await api.post('/seasonal-products', payload); toast.success('추가완료'); setEditId(res.data?.product?._id); }
      if (imageFile && editId) {
        const fd = new FormData(); fd.append('content', imageFile); fd.append('user', user._id);
        const up = await api.post('/content/upload', fd);
        if (up.data?.content?.url) await api.patch(`/seasonal-products/${editId}`, { productImageUrl: up.data.content.url });
      }
      setDialogOpen(false); fetchProducts();
    } catch (e) { toast.error(e.response?.data?.message || '저장 실패'); }
  };

  const handleDelete = async (id) => { if (!window.confirm('삭제하시겠습니까?')) return; await api.patch(`/seasonal-products/${id}`, { isActive: false }); fetchProducts(); };
  const fmt = (cents) => cents ? `$${(cents / 100).toFixed(2)}` : '-';

  // 캠페인 일괄 적용
  const applyCampaignToStores = async (campaignKey) => {
    if (selectedStores.length === 0) return toast.error('매장을 선택하세요');
    const campaignProducts = products.filter(p => p.campaign === campaignKey);
    if (campaignProducts.length === 0) return toast.error('캠페인에 제품이 없습니다');
    try {
      for (const storeId of selectedStores) {
        await api.put(`/screen-config/${storeId}`, {
          screens: [{ screenIndex: 1, displayType: 'S', rightType: 'seasonal', seasonalProductIds: campaignProducts.map(p => p._id) }],
        });
      }
      toast.success(`${selectedStores.length}개 매장에 ${campaignKey} 캠페인 적용 완료`);
      setSelectedStores([]);
    } catch (e) { toast.error('적용 실패'); }
  };

  const filteredStores = stores.filter(s =>
    (s.name || '').toLowerCase().includes(storeSearch.toLowerCase()) ||
    (s.town || '').toLowerCase().includes(storeSearch.toLowerCase())
  );

  return (
    <Layout title="Seasonal" user={user}>
      <Box sx={{ maxWidth: 1100, mx: 'auto', p: 2 }}>
        <SeasonalTabs />
        <Typography sx={{ fontSize: 22, fontWeight: 700, mb: 2 }}>Seasonal Management</Typography>

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}>
          <Tab icon={<CampaignIcon />} iconPosition="start" label="캠페인" />
          <Tab icon={<ImageIcon />} iconPosition="start" label="제품 관리" />
          <Tab icon={<TuneIcon />} iconPosition="start" label="매장 커스텀" />
          <Tab icon={<VideoLibraryIcon />} iconPosition="start" label="영상" />
        </Tabs>

        {/* ═══ TAB 0: 캠페인 메인 ═══ */}
        {tab === 0 && (
          <Box>
            <Typography sx={{ fontSize: 13, color: '#888', mb: 2 }}>캠페인별 시즈널 패널을 미리보고 매장에 일괄 적용할 수 있습니다.</Typography>
            <Grid container spacing={2}>
              {CAMPAIGNS.map(c => {
                const campProducts = products.filter(p => p.campaign === c.key);
                return (
                  <Grid item xs={12} sm={6} md={4} key={c.key}>
                    <Paper sx={{ borderRadius: 3, overflow: 'hidden', border: '2px solid #e5e7eb', '&:hover': { borderColor: c.color } }}>
                      <Box sx={{ height: 8, background: c.color }} />
                      <Box sx={{ p: 2 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 0.5 }}>{c.label}</Typography>
                        <Typography sx={{ fontSize: 12, color: '#888', mb: 1 }}>{campProducts.length}개 제품</Typography>
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1.5 }}>
                          {campProducts.slice(0, 4).map(p => (
                            <Box key={p._id} sx={{ width: 48, height: 48, borderRadius: 1, background: '#f5f0e8', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                              {p.productImageUrl ? (
                                <img src={p.productImageUrl?.startsWith('/') ? p.productImageUrl : `${imgUrl}/${p.productImageUrl}`} alt={p.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                              ) : <ImageIcon sx={{ fontSize: 20, color: '#ccc' }} />}
                            </Box>
                          ))}
                          {campProducts.length > 4 && <Chip label={`+${campProducts.length - 4}`} size="small" sx={{ fontSize: 10, height: 20 }} />}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 0.5 }}>
                          <Button size="small" variant="outlined" startIcon={<PreviewIcon sx={{ fontSize: 14 }} />}
                            onClick={() => { setPreviewUrl(c.htmlPanel); setPreviewOpen(true); }}
                            sx={{ fontSize: 10, flex: 1 }}>미리보기</Button>
                          <Button size="small" variant="contained" startIcon={<SendIcon sx={{ fontSize: 14 }} />}
                            onClick={() => { setTab(2); setFilterCampaign(c.key); }}
                            sx={{ fontSize: 10, flex: 1, background: c.color }}>매장 적용</Button>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}

        {/* ═══ TAB 1: 제품 관리 ═══ */}
        {tab === 1 && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1, flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Campaign</InputLabel>
                <Select value={filterCampaign} label="Campaign" onChange={e => setFilterCampaign(e.target.value)}>
                  <MenuItem value="">All</MenuItem>
                  {CAMPAIGNS.map(c => <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>)}
                </Select>
              </FormControl>
              <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd} sx={{ background: '#551FFF', borderRadius: 2 }}>Add Product</Button>
            </Box>

            {loading ? <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box> : (
              <Grid container spacing={2}>
                {products.map(p => (
                  <Grid item xs={6} sm={4} md={3} key={p._id}>
                    <Paper sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid #e5e7eb' }}>
                      <Box sx={{ height: 140, background: '#f5f0e8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {p.productImageUrl ? (
                          <img src={p.productImageUrl?.startsWith('/') ? p.productImageUrl : `${imgUrl}/${p.productImageUrl}`} alt={p.name} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                        ) : <ImageIcon sx={{ fontSize: 40, color: '#ccc' }} />}
                      </Box>
                      <Box sx={{ p: 1.5 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: 12, mb: 0.3 }}>{p.name}</Typography>
                        <Chip label={p.campaignLabel || p.campaign} size="small" sx={{ fontSize: 9, height: 18, mb: 0.5 }} />
                        <Box sx={{ fontSize: 10, color: '#555' }}>HOT {fmt(p.prices?.hot_s)}/{fmt(p.prices?.hot_m)} · ICE {fmt(p.prices?.ice_s)}/{fmt(p.prices?.ice_m)}</Box>
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                          <IconButton size="small" onClick={() => openEdit(p)}><EditIcon sx={{ fontSize: 16 }} /></IconButton>
                          <IconButton size="small" onClick={() => handleDelete(p._id)} sx={{ color: '#e53935' }}><DeleteOutlineIcon sx={{ fontSize: 16 }} /></IconButton>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            )}
          </Box>
        )}

        {/* ═══ TAB 2: 매장 커스텀 ═══ */}
        {tab === 2 && (
          <Box>
            <Typography sx={{ fontSize: 13, color: '#888', mb: 1 }}>매장별로 시즈널 제품을 선택하고 가격을 커스텀하여 적용합니다.</Typography>

            {/* 제품 선택 */}
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 2 }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#551FFF', mb: 1 }}>적용할 제품 선택</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {products.map(p => {
                  const selected = customProducts.includes(p._id);
                  return (
                    <Chip key={p._id} label={p.name} size="small"
                      onClick={() => setCustomProducts(prev => selected ? prev.filter(id => id !== p._id) : [...prev, p._id])}
                      variant={selected ? 'filled' : 'outlined'}
                      sx={{ cursor: 'pointer', fontWeight: selected ? 700 : 400, ...(selected ? { background: '#551FFF', color: '#fff' } : {}) }} />
                  );
                })}
              </Box>
              {customProducts.length > 0 && (
                <Typography sx={{ fontSize: 11, color: '#551FFF', mt: 0.5 }}>{customProducts.length}개 선택됨</Typography>
              )}
            </Paper>

            {/* 매장 선택 */}
            <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 1 }}>
                <StorefrontIcon sx={{ fontSize: 16, color: '#551FFF' }} />
                <Typography sx={{ fontSize: 12, fontWeight: 700 }}>매장 선택</Typography>
                <TextField size="small" placeholder="검색..." value={storeSearch} onChange={e => setStoreSearch(e.target.value)}
                  sx={{ ml: 'auto', width: 200, '& .MuiInputBase-root': { fontSize: 12 } }} />
              </Box>
              <Box sx={{ maxHeight: 250, overflow: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ '& th': { fontSize: 11, fontWeight: 700, color: '#888' } }}>
                      <TableCell width={40}>
                        <Checkbox size="small" checked={selectedStores.length === filteredStores.length && filteredStores.length > 0}
                          onChange={e => setSelectedStores(e.target.checked ? filteredStores.map(s => s._id) : [])} />
                      </TableCell>
                      <TableCell>Store</TableCell>
                      <TableCell>주소</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredStores.slice(0, 50).map(s => (
                      <TableRow key={s._id} hover sx={{ cursor: 'pointer' }}
                        onClick={() => setSelectedStores(prev => prev.includes(s._id) ? prev.filter(id => id !== s._id) : [...prev, s._id])}>
                        <TableCell><Checkbox size="small" checked={selectedStores.includes(s._id)} /></TableCell>
                        <TableCell sx={{ fontSize: 12, fontWeight: 600 }}>{s.name}</TableCell>
                        <TableCell sx={{ fontSize: 11, color: '#555' }}>{[s.town, s.state].filter(Boolean).join(', ')}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
              <Typography sx={{ fontSize: 11, color: '#888', mt: 0.5 }}>{selectedStores.length}개 매장 선택</Typography>
            </Paper>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" disabled={selectedStores.length === 0 || customProducts.length === 0}
                onClick={async () => {
                  try {
                    for (const storeId of selectedStores) {
                      await api.put(`/screen-config/${storeId}`, {
                        screens: [{ screenIndex: 1, displayType: 'S', rightType: 'seasonal', seasonalProductIds: customProducts }],
                      });
                    }
                    toast.success(`${selectedStores.length}개 매장에 ${customProducts.length}개 제품 적용 완료`);
                    setSelectedStores([]); setCustomProducts([]);
                  } catch (e) { toast.error('적용 실패'); }
                }}
                sx={{ background: '#551FFF', px: 3 }}>
                {selectedStores.length}개 매장에 적용
              </Button>
            </Box>
          </Box>
        )}

        {/* ═══ TAB 3: 영상 ═══ */}
        {tab === 3 && (
          <Box>
            <Typography sx={{ fontSize: 13, color: '#888', mb: 2 }}>프로모션 영상을 업로드하고 매장 P 스크린에 배포합니다.</Typography>
            <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, textAlign: 'center' }}>
              <VideoLibraryIcon sx={{ fontSize: 48, color: '#ccc', mb: 1 }} />
              <Typography sx={{ fontSize: 14, fontWeight: 600, mb: 1 }}>프로모션 영상 관리</Typography>
              <Typography sx={{ fontSize: 12, color: '#888', mb: 2 }}>
                MP4 영상을 업로드하면 P 타입 스크린에 배포됩니다.
              </Typography>
              <Button component="label" variant="outlined" startIcon={<AddIcon />}>
                영상 업로드
                <input type="file" hidden accept="video/*" onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const fd = new FormData();
                  fd.append('content', file);
                  fd.append('name', file.name);
                  fd.append('userId', user?._id);
                  fd.append('role', user?.role || 'admin');
                  try {
                    await api.post('/content/upload', fd);
                    toast.success('영상 업로드 완료: ' + file.name);
                  } catch (err) { toast.error('업로드 실패'); }
                }} />
              </Button>
            </Paper>
          </Box>
        )}

        {/* ═══ Add/Edit Dialog ═══ */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ fontWeight: 700 }}>{editId ? 'Edit Product' : 'Add Seasonal Product'}</DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField size="small" label="Product Name *" value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
              <FormControl size="small">
                <InputLabel>Campaign</InputLabel>
                <Select value={form.campaign} label="Campaign" onChange={e => setForm(f => ({ ...f, campaign: e.target.value }))}>
                  {CAMPAIGNS.map(c => <MenuItem key={c.key} value={c.key}>{c.label}</MenuItem>)}
                </Select>
              </FormControl>
              <TextField size="small" label="Product Image" type="file" InputLabelProps={{ shrink: true }}
                inputProps={{ accept: 'image/*' }} onChange={e => setImageFile(e.target.files?.[0])} />
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#551FFF' }}>Prices ($)</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {['hot_s', 'hot_m', 'ice_s', 'ice_m'].map(k => (
                  <TextField key={k} size="small" type="number" label={k.replace('_', ' ').toUpperCase()}
                    value={form.prices[k]} inputProps={{ step: '0.05' }}
                    onChange={e => setForm(f => ({ ...f, prices: { ...f.prices, [k]: e.target.value } }))} sx={{ flex: 1 }} />
                ))}
              </Box>
              <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#555' }}>Calories</Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {['hot_s', 'hot_m', 'ice_s', 'ice_m'].map(k => (
                  <TextField key={k} size="small" type="number" label={k.replace('_', ' ').toUpperCase()}
                    value={form.calories[k]}
                    onChange={e => setForm(f => ({ ...f, calories: { ...f.calories, [k]: e.target.value } }))} sx={{ flex: 1 }} />
                ))}
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSave} sx={{ background: '#551FFF' }}>Save</Button>
          </DialogActions>
        </Dialog>

        {/* ═══ Preview Dialog ═══ */}
        <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle sx={{ fontWeight: 700 }}>캠페인 패널 미리보기</DialogTitle>
          <DialogContent>
            {previewUrl && (
              <iframe src={previewUrl} style={{ width: '100%', height: 500, border: '1px solid #eee', borderRadius: 8 }} title="preview" />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPreviewOpen(false)}>닫기</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Layout>
  );
}

export default SeasonalProducts;
